import axios from 'axios';
import Link from 'next/link';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { bugsnag } from 'shared/utils/services/bugsnag';
import { buildRequestUrl } from 'homepage/utils/buildRequestUrl';
import { buildSectionLinkUrl } from 'homepage/utils/buildSectionLinkUrl';
import { Caret } from 'shared/icons/Caret';
import { fetchVerticalListSection } from 'homepage/services/homepageApi';
import { hideScrollbars } from 'styles/utils/hideScrollbars';
import { RankedOrPriceListCard } from 'homepage/components/verticalListCarouselSection/RankedOrPriceListCard';
import { variableFontRegular } from 'styles/theme/text';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { useIsElementInViewport } from 'shared/hooks/useIsElementInViewport';
import { useAbortController } from 'shared/hooks/useAbortController';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { useTriggerSwipeEvent } from 'homepage/hooks/useTriggerSwipeEvent';
import { VERTICAL_LIST_CAROUSEL_PRICE_LIST_SECTION } from 'homepage/enums/contentConstants';

import { IConstructorBrowseResponse } from 'shared/types/ConstructorBrowseResponse';
import { IConstructorUserParams } from 'search/types/ConstructorUserParams';
import { IConstructorGroup } from 'shared/types/ConstructorGroup';
import { IConstructorResult } from 'shared/types/ConstructorResult';
import { IModuleTrackingData } from 'homepage/types/ModuleTrackingData';
import { ISearchConfig } from 'homepage/types/SectionSearchConfig';
import { DESKTOP_CATEGORY_CARD_WIDTH } from 'homepage/styles/constants';

export const VerticalListSection = ({
  constructorResponse,
  constructorUserParams,
  id,
  title,
  type,
  searchConfig,
  moduleTrackingData,
  onClick,
}: {
  constructorUserParams?: IConstructorUserParams;
  constructorResponse?: IConstructorBrowseResponse;
  id?: number;
  title?: string;
  type: string;
  searchConfig?: ISearchConfig;
  moduleTrackingData: IModuleTrackingData;
  onClick: () => void;
}) => {
  const moduleRef = useRef();
  const { signal } = useAbortController()();
  const initialInViewport = useIsElementInViewport(moduleRef);

  const [isModuleInViewport, setIsModuleInViewport] = useState(initialInViewport);
  const [wasSSRed] = useState(!!constructorResponse);

  const { trackScrollEvent } = useTriggerSwipeEvent(moduleTrackingData);
  const { isLoading } = useStoreSelector((state) => state.homepage);

  const [currentConstructorResponse, setCurrentConstructorResponse] = useState(constructorResponse);

  /**
   * [reFetchPriceModulesToBustCachedPrices]
   * this method exists to make a fresh request to get any section
   * of type PriceListSection. We do this so that we can cache the homepage content
   * for a period of time, but always maintain super fresh prices in this section
   */

  useEffect(() => {
    const getAllVerticalListTypesLists = async (abortSignal) => {
      const response = await fetchVerticalListSection(
        searchConfig,
        type,
        constructorUserParams,
        abortSignal,
      );
      setCurrentConstructorResponse(response);
    };
    if (!isModuleInViewport) {
      setIsModuleInViewport(initialInViewport);
    } else if (constructorResponse && !currentConstructorResponse) {
      setCurrentConstructorResponse(constructorResponse);
    } else if (
      isModuleInViewport &&
      !wasSSRed &&
      !isLoading &&
      !currentConstructorResponse &&
      !constructorResponse
    ) {
      void getAllVerticalListTypesLists(signal);
    }
  }, [
    initialInViewport,
    isModuleInViewport,
    isLoading,
    currentConstructorResponse,
    wasSSRed,
    constructorResponse,
  ]);

  useEffect(() => {
    const reFetchPriceModulesToBustCachedPrices = async (abortSignal) => {
      try {
        if (type === VERTICAL_LIST_CAROUSEL_PRICE_LIST_SECTION) {
          const requestUrl = buildRequestUrl({ searchConfig });

          const newConstructorResponse = await axios
            .get(requestUrl, { headers: constructorUserParams, signal: abortSignal })
            .then((response) => {
              return response.data;
            });

          if (newConstructorResponse) {
            setCurrentConstructorResponse(newConstructorResponse);
          }
        }
      } catch (error) {
        bugsnag(error, { context: 'Refetch Homepage Price List Section to Refresh Prices' });
      }
    };
    if (isModuleInViewport && wasSSRed) {
      void reFetchPriceModulesToBustCachedPrices(signal);
    }
  }, []);

  if (!currentConstructorResponse?.response?.results) {
    return (
      <VerticalListSection.Container ref={moduleRef} $isModuleInViewport={isModuleInViewport} />
    );
  }

  const {
    response: { groups, results },
  }: { response?: { groups?: Partial<IConstructorGroup>[]; results?: IConstructorResult[] } } =
    currentConstructorResponse;

  const linkUrl = buildSectionLinkUrl(searchConfig, groups);

  const handleSubSectionItemClick = (index, subsection) => {
    sendTrackingEvent('CMS_MODULE_ITEM_TAP', {
      ...moduleTrackingData,
      item_position: index,
      product_template_slug: subsection?.data.slug,
    });
  };

  return (
    <VerticalListSection.Container
      data-qa="masonry_section_link"
      key={id}
      onClick={onClick}
      onScroll={trackScrollEvent}
      ref={moduleRef}
      $isModuleInViewport={isModuleInViewport}
    >
      <VerticalListSection.Middle>
        <Link href={linkUrl} passHref prefetch={false}>
          <VerticalListSection.TitleAndArrowContainer onClick={onClick}>
            <VerticalListSection.Title>{title}</VerticalListSection.Title>
            <VerticalListSection.CaretContainer>
              <Caret color="white" direction="right" size={8} />
            </VerticalListSection.CaretContainer>
          </VerticalListSection.TitleAndArrowContainer>
        </Link>
        {results?.map((item, index) => (
          <RankedOrPriceListCard
            isHomepage
            key={`${item?.value}-${item?.data?.slug}`}
            isModuleInViewport={isModuleInViewport}
            position={index + 1}
            type={type}
            {...item}
            onClick={() => handleSubSectionItemClick(index, item)}
          />
        ))}
      </VerticalListSection.Middle>
    </VerticalListSection.Container>
  );
};

VerticalListSection.Container = styled.div<{ $isModuleInViewport: boolean }>`
  align-items: center;
  aspect-ratio: 1.51/4;
  border: ${({ theme }) => theme.borders.opaqueWhite};
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  height: auto;
  margin-right: 8px;
  max-width: 45vw;
  min-width: 45vw;
  max-height: 98.7vw;
  min-height: 98.7vw;
  overflow: scroll;
  padding: 24px 0px;
  position: relative;
  width: 45vw;
  ${hideScrollbars};

  img {
    ${({ $isModuleInViewport }) => !$isModuleInViewport && `min-width: 64px;`};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    aspect-ratio: 3/4;
    margin-right: 24px;
    padding: 24px 0px;
    max-height: 30.2vw;
    min-height: 30.2vw;
    width: 23.5vw;

    // calculating the exact margin so the cards are aligned with CategoryListSection
    min-width: ${DESKTOP_CATEGORY_CARD_WIDTH};
    max-width: ${DESKTOP_CATEGORY_CARD_WIDTH};
  }
`;

VerticalListSection.Title = styled.h3`
  font-weight: 600;
  position: sticky;
  text-align: center;
  top: 0;
  margin: 0;
  ${variableFontRegular({ size: '11.5px', height: '14px' })};
  &:hover {
    text-decoration: underline;
  }
`;

VerticalListSection.Middle = styled.div`
  width: 100%;
`;

VerticalListSection.TitleAndArrowContainer = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
`;

VerticalListSection.CaretContainer = styled.div`
  margin-left: 4px;
  display: flex;
`;
